import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import tw from "twin.macro";
import Modal from "react-modal";
import GradientButton from "../../components/GradientButton";

const COLORS = {
  teal: "#437672",
  orange: "#A4400A",
  body: "#4D4B4B",
};

const LOGIN_URL = "https://www.trataz.com.br/login";

const FaqModalRoot = styled(Modal)`
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  width: min(1120px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 8px;
  outline: none;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.24);
`;

const ModalInner = styled.div`
  ${tw`relative`}
  padding: 2.5rem 1.5rem 2.6rem;
  background: #fff;

  @media (min-width: 1024px) {
    padding: 4.25rem 4.2rem 3.3rem;
  }
`;

const ModalClose = styled.button`
  ${tw`absolute`}
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.06);
  color: #444;
  font-size: 20px;
  cursor: pointer;
  z-index: 3;
`;

const Header = styled.div`
  ${tw`grid gap-6 items-center`}
  max-width: 1000px;
  margin: 0 auto;

  @media (min-width: 1024px) {
    grid-template-columns: 204px 1fr 129px;
    column-gap: 1.15rem;
    align-items: end;
  }
`;

const TratiImage = styled.img`
  width: 180px;
  max-width: 100%;
  height: 212px;
  object-fit: cover;
  object-position: top center;
  display: block;
  margin: 0 auto;

  @media (min-width: 1024px) {
    width: 204px;
    height: 240px;
    margin: 0;
    transform: translateY(1px);
  }
`;

const HeaderText = styled.div`
  text-align: center;

  @media (min-width: 1024px) {
    text-align: left;
    align-self: end;
    padding-bottom: 2.1rem;
  }
`;

const TratiTitle = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 40px;
  line-height: 1.02;
  font-weight: 600;
  color: ${COLORS.teal};
  text-transform: uppercase;

  @media (min-width: 1024px) {
    font-size: 40px;
  }

  @media (max-width: 767px) {
    font-size: 32px;
  }
`;

const FaqTitle = styled.div`
  margin-top: 0.2rem;
  font-family: "Montserrat", sans-serif;
  font-size: 34px;
  line-height: 1;
  font-weight: 400;
  color: ${COLORS.body};
  text-transform: uppercase;

  @media (min-width: 1024px) {
    font-size: 38px;
    white-space: nowrap;
  }

  @media (max-width: 767px) {
    font-size: 26px;
  }
`;

const BrandLogo = styled.img`
  width: 118px;
  height: auto;
  display: block;
  margin: 0 auto;

  @media (min-width: 1024px) {
    width: 129px;
    margin-left: auto;
    margin-right: 0;
    align-self: center;
    transform: translateY(-1.8rem);
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const Divider = styled.div`
  max-width: 1000px;
  margin: 1.4rem 0 2rem;
  border-top: 2px solid #d9d9d9;

  @media (min-width: 1024px) {
    margin: 0 auto 2.2rem;
    border-top-width: 4px;
  }
`;

const FaqList = styled.div`
  ${tw`grid`}
  max-width: 1000px;
  margin: 0 auto;
  gap: 2.25rem;
`;

const FaqItem = styled.div``;

const Question = styled.h3`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.15;
  font-weight: 500;
  color: ${COLORS.teal};
  text-transform: uppercase;

  @media (min-width: 1024px) {
    font-size: 20px;
  }
`;

const Answer = styled.p`
  margin: 0.15rem 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 18px;
  line-height: 1.18;
  font-weight: 400;
  color: ${COLORS.body};

  @media (min-width: 1024px) {
    font-size: 20px;
  }
`;

const BottomBar = styled.div`
  ${tw`flex justify-center`}
  margin-top: 3.5rem;
`;

const PrimaryButton = styled(GradientButton)`
  min-width: 297px;
  height: 45px;
  padding: 0 30px;
  font-family: "Montserrat", sans-serif;
  font-size: 20px;
  line-height: 1.1;
  font-weight: 500;

  @media (max-width: 767px) {
    font-size: 16px;
  }
`;

export default function FaqModal({ isOpen, onRequestClose, items }) {
  return (
    <FaqModalRoot
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnOverlayClick
      overlayClassName="plans-modal-overlay"
    >
      <ModalInner>
        <ModalClose onClick={onRequestClose} aria-label="Fechar modal">
          ×
        </ModalClose>

        <Header>
          <TratiImage src="/novas-imagens/section-six/trati.png" alt="Trati" />

          <HeaderText>
            <TratiTitle>
              TRATI
              <br />
              RESPONDE!
            </TratiTitle>
            <FaqTitle>PERGUNTAS FREQUENTES</FaqTitle>
          </HeaderText>

          <BrandLogo src="/novas-imagens/logo.png" alt="Trataz" />
        </Header>

        <Divider />

        <FaqList>
          {items.map((item) => (
            <FaqItem key={item.title}>
              <Question>{item.title}</Question>
              <Answer>{item.description}</Answer>
            </FaqItem>
          ))}
        </FaqList>

        <BottomBar>
          <PrimaryButton
            as={Link}
            to={LOGIN_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            COMEÇAR GRATUITAMENTE
          </PrimaryButton>
        </BottomBar>
      </ModalInner>
    </FaqModalRoot>
  );
}
